import { AuthResponse } from "../types";

interface EventSelectionProps {
  events: AuthResponse[];
  onEventSelect: (eventData: AuthResponse) => void;
  onBack: () => void;
}

export function EventSelection({
  events,
  onEventSelect,
  onBack,
}: EventSelectionProps) {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "No date set";
    // Date from API is already in ISO format with time
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: 'UTC'
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return "";
    return timeString;
  };

  const getEventStatus = (rsvpStatus: string) => {
    switch (rsvpStatus) {
      case "GOING":
        return { label: "Going", class: "status-going", icon: "✅" };
      case "GOING_WITH_GUESTS":
        return {
          label: "Going with Guests",
          class: "status-going-with-guests",
          icon: "👥",
        };
      default:
        return { label: "Pending", class: "status-pending", icon: "⏳" };
    }
  };

  return (
    <div className="event-selection-container">
      <div className="event-selection-card">
        {/* Header */}
        <div className="event-selection-header">
          <button className="back-button" onClick={onBack}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 12H5M12 19l-7-7 7-7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="header-content">
            <h1 className="selection-title">Choose Your Event</h1>
            <p className="selection-subtitle">
              You have access to {events.length} event
              {events.length > 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {/* Events List */}
        <div className="events-list">
          {events.map((eventData) => {
            const status = getEventStatus(eventData.guest.rsvpStatus);
            return (
              <div
                key={eventData.event.id}
                className="event-option"
                onClick={() => onEventSelect(eventData)}
              >
                {/* Event Poster Preview */}
                <div className="event-poster-preview">
                  {eventData.event.posterUrl ? (
                    <img
                      src={eventData.event.posterUrl}
                      alt={eventData.event.name}
                      className="poster-thumbnail"
                    />
                  ) : (
                    <div className="poster-placeholder">
                      <span className="placeholder-icon">🎉</span>
                    </div>
                  )}
                </div>

                {/* Event Details */}
                <div className="event-option-details">
                  <div className="event-option-header">
                    <h3 className="event-option-title">
                      {eventData.event.name}
                    </h3>
                    <div className={`event-status ${status.class}`}>
                      <span className="status-icon">{status.icon}</span>
                      <span className="status-label">{status.label}</span>
                    </div>
                  </div>

                  {eventData.event.description && (
                    <p className="event-option-description">
                      {eventData.event.description.length > 100
                        ? `${eventData.event.description.substring(0, 100)}...`
                        : eventData.event.description}
                    </p>
                  )}

                  <div className="event-option-meta">
                    {eventData.event.date && (
                      <div className="meta-item">
                        <span className="meta-icon">📅</span>
                        <span className="meta-text">
                          {formatDate(eventData.event.date)}
                        </span>
                      </div>
                    )}

                    {eventData.event.time && (
                      <div className="meta-item">
                        <span className="meta-icon">🕐</span>
                        <span className="meta-text">
                          {formatTime(eventData.event.time)}
                        </span>
                      </div>
                    )}

                    {eventData.event.location && (
                      <div className="meta-item">
                        <span className="meta-icon">📍</span>
                        <span className="meta-text">
                          {eventData.event.location}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Selection Arrow */}
                <div className="selection-arrow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12h14M12 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="event-selection-footer">
          <p className="footer-text">
            Select an event to view details and manage your RSVP
          </p>
        </div>
      </div>

      <style>{`
        .event-selection-container {
          min-height: 100vh;
          min-height: 100dvh;
          background: linear-gradient(135deg, #2C5F5D 0%, #1a4a47 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
          overflow: hidden;
        }

        .bg-animation {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          z-index: 1;
        }

        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          animation: float 8s ease-in-out infinite;
          backdrop-filter: blur(1px);
        }

        .shape-1 {
          width: 100px;
          height: 100px;
          top: 15%;
          left: 8%;
          animation-delay: 0s;
        }

        .shape-2 {
          width: 140px;
          height: 140px;
          top: 65%;
          right: 8%;
          animation-delay: 3s;
        }

        .shape-3 {
          width: 80px;
          height: 80px;
          bottom: 15%;
          left: 15%;
          animation-delay: 6s;
        }

        .shape-4 {
          width: 120px;
          height: 120px;
          top: 8%;
          right: 25%;
          animation-delay: 2s;
        }

        @keyframes float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg) scale(1); 
            opacity: 0.6;
          }
          50% { 
            transform: translateY(-30px) rotate(180deg) scale(1.1); 
            opacity: 0.8;
          }
        }

        .event-selection-card {
          background: rgba(255, 255, 255, 0.75); /* Glassmorphic */
          backdrop-filter: blur(30px) saturate(150%);
          -webkit-backdrop-filter: blur(30px) saturate(150%);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 20px;
          box-shadow: 
            0 20px 40px -12px rgba(0, 0, 0, 0.15);
          max-width: 480px;
          width: calc(100% - 2rem);
          max-height: 85vh;
          overflow: hidden;
          position: relative;
          z-index: 2;
          margin: 1rem;
        }

        .event-selection-header {
          display: flex;
          align-items: center;
          padding: 2rem 1.5rem 1.5rem;
          background: linear-gradient(135deg, rgba(44, 95, 93, 0.85) 0%, rgba(26, 74, 71, 0.85) 100%);
          backdrop-filter: blur(20px) saturate(140%);
          -webkit-backdrop-filter: blur(20px) saturate(140%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
        }

        .back-button {
          background: rgba(255, 255, 255, 0.25); /* Enhanced glassmorphic */
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          padding: 0.75rem;
          border-radius: 12px;
          color: white;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          margin-right: 1rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15),
                      0 0 0 1px rgba(255, 255, 255, 0.2) inset;
        }

        .back-button:hover {
          background: rgba(255, 255, 255, 0.35);
          color: white;
          transform: translateX(-2px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25),
                      0 0 0 1px rgba(255, 255, 255, 0.3) inset;
        }

        .back-button:active {
          transform: translateX(0px);
        }

        .back-button svg {
          width: 20px;
          height: 20px;
        }

        .header-content {
          flex: 1;
        }

        .selection-title {
          font-size: 1.75rem;
          font-weight: 600;
          color: white;
          margin: 0 0 0.5rem 0;
          letter-spacing: -0.025em;
        }

        .selection-subtitle {
          font-size: 0.95rem;
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
          font-weight: 500;
        }

        .events-list {
          max-height: 55vh;
          overflow-y: auto;
          padding: 1.5rem;
          scrollbar-width: thin;
          scrollbar-color: rgba(203, 213, 225, 0.5) transparent;
        }

        .events-list::-webkit-scrollbar {
          width: 6px;
        }

        .events-list::-webkit-scrollbar-track {
          background: transparent;
        }

        .events-list::-webkit-scrollbar-thumb {
          background: rgba(203, 213, 225, 0.5);
          border-radius: 3px;
        }

        .events-list::-webkit-scrollbar-thumb:hover {
          background: rgba(203, 213, 225, 0.7);
        }

        .event-option {
          display: flex;
          align-items: center;
          padding: 1.25rem;
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 16px;
          margin-bottom: 1rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: rgba(255, 255, 255, 0.7); /* More transparent glassmorphic */
          backdrop-filter: blur(25px) saturate(140%);
          -webkit-backdrop-filter: blur(25px) saturate(140%);
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(44, 95, 93, 0.15),
                      0 0 0 1px rgba(255, 255, 255, 0.2) inset;
        }

        .event-option::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(44, 95, 93, 0.05) 0%, rgba(26, 74, 71, 0.05) 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .event-option:hover {
          border-color: rgba(255, 255, 255, 0.5);
          background: rgba(255, 255, 255, 0.75);
          box-shadow: 
            0 12px 32px rgba(44, 95, 93, 0.25),
            0 0 0 1px rgba(255, 255, 255, 0.3) inset;
          transform: translateY(-3px) scale(1.02);
        }

        .event-option:hover::before {
          opacity: 1;
        }

        .event-option:active {
          transform: translateY(-1px) scale(1.01);
        }

        .event-option:last-child {
          margin-bottom: 0;
        }

        .event-poster-preview {
          width: 70px;
          height: 70px;
          border-radius: 12px;
          overflow: hidden;
          margin-right: 1.25rem;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.8);
        }

        .poster-thumbnail {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .event-option:hover .poster-thumbnail {
          transform: scale(1.05);
        }

        .poster-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #2C5F5D 0%, #1a4a47 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .placeholder-icon {
          font-size: 1.75rem;
        }

        .event-option-details {
          flex: 1;
          min-width: 0;
          position: relative;
          z-index: 1;
        }

        .event-option-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.75rem;
        }

        .event-option-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #2C5F5D;
          margin: 0;
          line-height: 1.3;
          letter-spacing: -0.025em;
        }

        .event-status {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.375rem 0.75rem;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 600;
          flex-shrink: 0;
          backdrop-filter: blur(15px) saturate(130%);
          -webkit-backdrop-filter: blur(15px) saturate(130%);
          border: 1px solid rgba(255, 255, 255, 0.4);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1),
                      0 0 0 1px rgba(255, 255, 255, 0.2) inset;
        }

        .status-going {
          background: rgba(44, 95, 93, 0.3); /* More visible glassmorphic */
          color: #2C5F5D;
          border-color: rgba(44, 95, 93, 0.5);
        }

        .status-going-with-guests {
          background: rgba(26, 74, 71, 0.3); /* More visible glassmorphic */
          color: #1a4a47;
          border-color: rgba(26, 74, 71, 0.5);
        }

        .status-pending {
          background: rgba(245, 158, 11, 0.25); /* More visible glassmorphic */
          color: #d97706;
          border-color: rgba(245, 158, 11, 0.4);
        }

        .status-icon {
          font-size: 0.8rem;
        }

        .event-option-description {
          font-size: 0.9rem;
          color: #64748b;
          margin: 0 0 0.875rem 0;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .event-option-meta {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 500;
        }

        .meta-icon {
          font-size: 0.8rem;
          width: 14px;
          text-align: center;
          opacity: 0.8;
        }

        .selection-arrow {
          color: #cbd5e1;
          margin-left: 0.75rem;
          flex-shrink: 0;
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
        }

        .selection-arrow svg {
          width: 18px;
          height: 18px;
        }

        .event-option:hover .selection-arrow {
          color: #2C5F5D;
          transform: translateX(4px);
        }

        .event-selection-footer {
          padding: 1.5rem;
          border-top: 1px solid rgba(44, 95, 93, 0.1);
          background: linear-gradient(135deg, rgba(44, 95, 93, 0.05) 0%, rgba(26, 74, 71, 0.05) 100%);
          backdrop-filter: blur(10px);
        }

        .footer-text {
          font-size: 0.8rem;
          color: #64748b;
          text-align: center;
          margin: 0;
          font-weight: 500;
          line-height: 1.4;
        }

        /* Mobile responsiveness */
        @media (max-width: 640px) {
          .event-selection-card {
            max-height: 92vh;
            border-radius: 20px;
            width: calc(100% - 1.5rem);
            margin: 0.75rem;
          }

          .event-selection-header {
            padding: 1.5rem 1.25rem 1.25rem;
          }

          .selection-title {
            font-size: 1.5rem;
          }

          .selection-subtitle {
            font-size: 0.9rem;
          }

          .events-list {
            padding: 1.25rem;
            max-height: 65vh;
          }

          .event-option {
            padding: 1rem;
            border-radius: 14px;
          }

          .event-poster-preview {
            width: 60px;
            height: 60px;
            margin-right: 1rem;
          }

          .event-option-title {
            font-size: 1rem;
          }

          .event-option-description {
            font-size: 0.85rem;
          }

          .meta-item {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 480px) {
          .event-selection-card {
            width: calc(100% - 1rem);
            margin: 0.5rem;
          }

          .event-selection-header {
            padding: 1.25rem 1rem 1rem;
          }

          .events-list {
            padding: 1rem;
          }

          .event-option {
            padding: 0.875rem;
          }

          .event-poster-preview {
            width: 55px;
            height: 55px;
            margin-right: 0.875rem;
          }
        }
      `}</style>
    </div>
  );
}
